import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Card, CardContent, Chip, Button, Tabs, Tab, Alert, LinearProgress, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Link, Divider,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { MY_VENDOR_SELECTIONS_QUERY, CANCEL_VENDOR_SELECTION_MUTATION } from '../graphql/queries';
import { BookingStatusChip, EditBookingDialog, fmtBookingDate, countdown, quantityText } from '../components/VendorBooking';

import Paged from '../components/Paged'; // lists beyond 10 are paged
import AccountManagerCard from '../components/AccountManagerCard';
import BookingProgress from '../components/BookingProgress';
const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

// The family's booked vendors: upcoming dates, what needs attention, and history
export default function MyVendorsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('upcoming');
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState(null);
  const { data, loading, error, refetch } = useQuery(MY_VENDOR_SELECTIONS_QUERY, { variables: { includeClosed: true }, fetchPolicy: 'cache-and-network' });
  const [cancel, { loading: cancelBusy }] = useMutation(CANCEL_VENDOR_SELECTION_MUTATION);

  const all = data?.myVendorSelections || [];
  const upcoming = all.filter(b => ['requested', 'confirmed'].includes(b.status) && b.daysUntil >= 0);
  const attention = all.filter(b => b.status === 'declined' && b.daysUntil >= 0);
  const past = all.filter(b => !upcoming.includes(b) && !attention.includes(b));
  const lists = { upcoming, attention, past };
  const shown = lists[tab];

  const doCancel = async () => {
    try {
      await cancel({ variables: { id: cancelling.id, reason: reason || null } });
      setMessage({ severity: 'info', text: `Booking with ${cancelling.businessName} cancelled. The vendor has been notified.` });
      setCancelling(null); setReason(''); refetch();
    } catch (e) { setMessage({ severity: 'error', text: e.message }); }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>My Vendors</Typography>
          <Typography variant="body2" color="text.secondary">
            Vendors you've booked for your wedding services, with their dates. You're reminded 30, 7 and 1 day(s) before and on the day.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => navigate('/dashboard/service-providers')}>Find vendors</Button>
      </Stack>

      <AccountManagerCard sx={{ mb: 2 }} />
      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}
      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="upcoming" label={`Upcoming (${upcoming.length})`} />
        <Tab value="attention" label={`Needs attention (${attention.length})`} />
        <Tab value="past" label={`Past & closed (${past.length})`} />
      </Tabs>

      {!loading && shown.length === 0 && (
        <Alert severity="info">
          {tab === 'upcoming' ? 'No upcoming bookings yet. Open Service Providers and click "Book" on a vendor you like.'
            : tab === 'attention' ? 'Nothing needs your attention.' : 'No past or cancelled bookings.'}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Paged items={shown}>{rows => rows.map(b => (
          <Grid item xs={12} lg={6} key={b.id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Chip size="small" color="primary" variant="outlined" label={b.categoryName} sx={{ mb: 0.5 }} />
                    <Typography variant="h6" fontWeight={700}>{b.businessName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {b.offeringTitle}{b.packageName ? ` · ${b.packageName}` : ''}
                    </Typography>
                  </Box>
                  <BookingStatusChip status={b.status} />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" mt={1.5} flexWrap="wrap" useFlexGap>
                  <EventIcon fontSize="small" color="action" />
                  <Typography fontWeight={600}>{fmtBookingDate(b.requiredDate)}{b.requiredTime ? `, ${b.requiredTime}` : ''}</Typography>
                  {['requested', 'confirmed'].includes(b.status) && (
                    <Chip size="small" label={countdown(b.daysUntil)} color={b.daysUntil <= 7 ? 'warning' : 'default'} />
                  )}
                </Stack>
                <Typography variant="body2" mt={1}>
                  {b.price != null && <>{inr(b.price)} {b.priceUnit}</>}
                  {b.quantity ? ` · ${quantityText(b.category, b.quantity)}` : ''}
                </Typography>
                {b.notes && <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>Your notes: {b.notes}</Typography>}
                {b.vendorNote && (
                  <Alert severity={b.status === 'declined' ? 'warning' : 'info'} sx={{ mt: 1 }}>Vendor: {b.vendorNote}</Alert>
                )}
                {/* The vendor's stages before the function, with their vouchers and photos */}
                <BookingProgress booking={b} defaultOpen={b.progress?.done > 1} />
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {b.contactPhone && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PhoneIcon fontSize="small" color="action" />
                      <Link variant="body2" href={`tel:${b.contactPhone}`}>{b.contactName ? `${b.contactName} · ` : ''}{b.contactPhone}</Link>
                    </Stack>
                  )}
                  {b.contactEmail && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <EmailIcon fontSize="small" color="action" />
                      <Link variant="body2" href={`mailto:${b.contactEmail}`}>{b.contactEmail}</Link>
                    </Stack>
                  )}
                </Stack>
                {['requested', 'confirmed'].includes(b.status) && (
                  <Stack direction="row" spacing={1} mt={2}>
                    <Button size="small" variant="outlined" onClick={() => setEditing(b)}>Change date / details</Button>
                    <Button size="small" color="error" onClick={() => setCancelling(b)}>Cancel booking</Button>
                  </Stack>
                )}
                {b.status === 'declined' && (
                  <Button size="small" variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/dashboard/service-providers')}>
                    Choose another vendor
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}</Paged>
      </Grid>

      <EditBookingDialog
        open={Boolean(editing)} booking={editing} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); setMessage({ severity: 'success', text: 'Booking updated. The vendor has been notified.' }); refetch(); }}
      />

      <Dialog open={Boolean(cancelling)} onClose={() => setCancelling(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel booking?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" mb={2}>
            {cancelling?.businessName} — {cancelling?.offeringTitle} on {cancelling && fmtBookingDate(cancelling.requiredDate)}. The vendor will be notified.
          </Typography>
          <TextField label="Reason (optional)" value={reason} onChange={e => setReason(e.target.value)} fullWidth size="small" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelling(null)}>Keep booking</Button>
          <Button color="error" variant="contained" onClick={doCancel} disabled={cancelBusy}>Cancel booking</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
